import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type TableOptions,
} from '@tanstack/react-table';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface SplitfinTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  title?: string;
  viewAllLink?: { label?: string; onClick: () => void };
  loading?: boolean;
  skeletonRows?: number;
  emptyState?: {
    icon?: React.ReactNode;
    title: string;
    description?: string;
  };
  onRowClick?: (row: TData) => void;
  className?: string;
  variant?: 'default' | 'dashboard-preview';
  getRowHref?: (row: TData) => string;
}

function SplitfinTable<TData>({
  columns,
  data,
  title,
  viewAllLink,
  loading = false,
  skeletonRows = 5,
  emptyState,
  onRowClick,
  className,
  variant = 'default',
  getRowHref,
}: SplitfinTableProps<TData>) {
  const tableOptions: TableOptions<TData> = {
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  };

  const table = useReactTable(tableOptions);

  return (
    <div
      className={cn(
        'border border-border bg-card overflow-hidden',
        variant === 'dashboard-preview' ? 'rounded-[var(--radius-panel)]' : 'rounded-xl',
        className,
      )}
    >
      {/* Title bar */}
      {(title || viewAllLink) && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
          {title && (
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          )}
          {viewAllLink && (
            <button
              onClick={viewAllLink.onClick}
              className={cn(
                "text-xs font-medium transition-colors",
                variant === 'dashboard-preview'
                  ? "text-primary hover:text-primary/80 group flex items-center gap-1 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] rounded-sm"
                  : "text-primary hover:text-primary/80"
              )}
            >
              {viewAllLink.label || 'View All'}
              {variant === 'dashboard-preview' && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="transition-transform duration-[var(--motion-fast)] ease-[var(--ease-out-standard)] group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5 motion-reduce:transition-none motion-reduce:transform-none"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              )}
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <Table className={cn(variant === 'default' && "table-fixed")}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              className="border-b border-border bg-muted/30 hover:bg-muted/30"
            >
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  style={variant === 'default' ? { width: `${header.getSize()}px` } : undefined}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
              {variant === 'dashboard-preview' && (
                <TableHead className="w-[var(--table-action-slot)] p-0" />
              )}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {loading ? (
            Array.from({ length: skeletonRows }).map((_, i) => (
              <TableRow
                key={`skeleton-${i}`}
                className={cn(
                  "hover:bg-transparent",
                  variant === 'dashboard-preview' && "h-[var(--table-preview-row-height)]"
                )}
              >
                {table.getAllColumns().map((col) => (
                  <TableCell key={col.id} style={variant === 'default' ? { width: `${col.getSize()}px` } : undefined}>
                    <Skeleton className="h-4 w-3/4" />
                  </TableCell>
                ))}
                {variant === 'dashboard-preview' && (
                  <TableCell className="w-[var(--table-action-slot)] p-0" />
                )}
              </TableRow>
            ))
          ) : table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(
                  'border-b border-border/30 transition-colors',
                  variant === 'dashboard-preview'
                    ? 'group relative h-[var(--table-preview-row-height)] hover:bg-[var(--table-row-hover)] focus-within:bg-[var(--table-row-hover)] active:bg-[var(--table-row-pressed)] motion-reduce:transition-none'
                    : 'table-row-hover',
                  onRowClick && 'cursor-pointer',
                )}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell, index) => {
                  const isPrimary = index === 0;
                  const href = getRowHref?.(row.original);
                  const content = flexRender(cell.column.columnDef.cell, cell.getContext());

                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(variant === 'dashboard-preview' && 'px-[var(--table-cell-inline)]')}
                      style={variant === 'default' ? { width: `${cell.column.getSize()}px` } : undefined}
                    >
                      {variant === 'dashboard-preview' && isPrimary && href ? (
                        <a
                          href={href}
                          className="focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] rounded-sm inline-block outline-none"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {content}
                        </a>
                      ) : (
                        content
                      )}
                    </TableCell>
                  );
                })}
                {variant === 'dashboard-preview' && (
                  <TableCell className="w-[var(--table-action-slot)] p-0 pr-[var(--table-cell-inline)] text-right align-middle">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="inline-block opacity-0 -translate-x-2 transition-all duration-[var(--motion-fast)] ease-[var(--ease-out-standard)] group-hover:opacity-100 group-hover:translate-x-0 group-focus-within:opacity-100 group-focus-within:translate-x-0 group-active:translate-x-0.5 text-muted-foreground motion-reduce:transition-none motion-reduce:transform-none"
                    >
                      <path d="m9 18 6-6-6-6"/>
                    </svg>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={variant === 'dashboard-preview' ? columns.length + 1 : columns.length}
                className="h-32 text-center"
              >
                <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                  {emptyState?.icon && (
                    <div className="text-muted-foreground/50">
                      {emptyState.icon}
                    </div>
                  )}
                  <p className="text-sm font-medium">
                    {emptyState?.title || 'No data'}
                  </p>
                  {emptyState?.description && (
                    <p className="text-xs text-muted-foreground/70">
                      {emptyState.description}
                    </p>
                  )}
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default SplitfinTable;
export type { SplitfinTableProps };
